import { Component, EventEmitter, OnInit, OnDestroy, Output, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription, take } from 'rxjs';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ProgressService } from '../services/progress.service';
import { AppMode } from '../models/app-mode.model';
import { TemplateRef, ViewChild } from '@angular/core';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import confetti from 'canvas-confetti';
import {
  getMilestoneRoute,
  getMilestoneQueryParams,
  ProgressMilestoneKey,
  ProgressMilestone,
  FieldMissingDetail,
  PropertyMissingDetail,
  MissingDetail,
} from './progress-navigation.utils';

@Component({
  selector: 'app-progress',
  standalone: true,
  imports: [
    NzProgressModule,
    NzIconModule,
    CommonModule,
    RouterModule,
    TranslateModule,
    NzButtonModule,
    NzCardModule,
    NzModalModule
  ],
  templateUrl: './progress.component.html',
  styleUrls: ['./progress.component.less']
})
export class ProgressComponent implements OnInit, OnDestroy {
  progress = 0;
  appMode: AppMode = 'emergency';
  milestones: ProgressMilestone[] = [];
  incompleteMilestones: ProgressMilestone[] = [];

  get isCompanyInfoComplete(): boolean {
    return (this.milestones.find(m => m.key === 'companyInfo')?.completed) ?? false;
  }

  get isLandingPageComplete(): boolean {
    return this.appMode === 'hospitality'
      ? (this.milestones.find(m => m.key === 'hospitalityLanding')?.completed) ?? false
      : (this.milestones.find(m => m.key === 'templateHeader')?.completed) ?? false;
  }

  get phaseOneCompletedCount(): number {
    return (this.isCompanyInfoComplete ? 1 : 0) + (this.isLandingPageComplete ? 1 : 0);
  }

  get allPhaseOneComplete(): boolean {
    return this.isCompanyInfoComplete && this.isLandingPageComplete;
  }

  get isPropertyStepUnlocked(): boolean {
    return this.allPhaseOneComplete;
  }

  get companyInfoMilestone(): ProgressMilestone | undefined {
    return this.milestones.find(m => m.key === 'companyInfo');
  }

  get landingPageMilestone(): ProgressMilestone | undefined {
    const key = this.appMode === 'hospitality' ? 'hospitalityLanding' : 'templateHeader';
    return this.milestones.find(m => m.key === key);
  }

  get propertyMilestone(): ProgressMilestone | undefined {
    const key = this.appMode === 'hospitality' ? 'hospitalityProperties' : 'properties';
    return this.milestones.find(m => m.key === key);
  }

  @Output() progressReached100 = new EventEmitter<boolean>();
  @Output() visibilityChange = new EventEmitter<boolean>();
  @ViewChild('onboardingVideoModal', { static: true }) onboardingVideoModal!: TemplateRef<any>;
  @ViewChild('missingStepsModal', { static: true }) missingStepsModal!: TemplateRef<any>;
  pendingTargetMilestone: ProgressMilestone | null = null;
  missingBeforeTarget: ProgressMilestone[] = [];
  onboardingVideoUrl!: SafeResourceUrl;
  justCompleted = false;
  loaded = false;
  isCompletingExit = false;
  private lastProgress: number | null = null;
  private readonly CELEBRATION_DURATION = 5000;
  private readonly EXIT_START_DELAY = 4200;
  private celebrationInProgress = false;
  private celebrateTimeoutId: any;
  private exitStartTimeoutId: any;
  private confettiTimeoutIds: any[] = [];
  private progressSubscription: Subscription | null = null;
  private reportedVisibility: boolean | null = null;

  constructor(private progressService: ProgressService,
              private message: NzMessageService,
              private translate: TranslateService,
              private router: Router,
              private ngZone: NgZone,
              private modal: NzModalService,
              private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.getProgress();
    this.progressSubscription = this.progressService.onProgressUpdate().subscribe(() => {
      this.getProgress();
    });
  }

  ngOnDestroy(): void {
    this.setProgressVisibility(false);
    clearTimeout(this.celebrateTimeoutId);
    clearTimeout(this.exitStartTimeoutId);
    this.confettiTimeoutIds.forEach(clearTimeout);
    this.confettiTimeoutIds = [];
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
      this.progressSubscription = null;
    }
  }

  getProgress() {
    this.progressService.getProgress().subscribe((data: any) => {
      const prevProgress = this.lastProgress; // null on first load

      this.progress = data.progress;
      this.loaded = true; // first API response arrived

      this.appMode = data.appMode === 'hospitality' ? 'hospitality' : 'emergency';
      this.milestones = Array.isArray(data.milestones)
        ? data.milestones.map((milestone: any) => ({
          ...milestone,
          missingSections: Array.isArray(milestone.missingSections) ? milestone.missingSections : []
        }) as ProgressMilestone)
        : [];

        this.incompleteMilestones = this.milestones.filter((milestone) => !milestone.completed);

      // ✅ Robust "just reached 100" detection:
      // 1) backend flag OR
      // 2) local transition from <100 to >=100 (fallback)
      const justReached100 =
        (!!data.hasJustReached100) ||
        (prevProgress !== null && prevProgress < 100 && this.progress >= 100);

      this.lastProgress = this.progress;

      if (justReached100 && !this.celebrationInProgress) {
        this.celebrationInProgress = true;
        this.justCompleted = true;

        // Run after DOM has applied the class so animation is visible
        setTimeout(() => this.triggerConfetti(), 0);

        this.message.success(this.translate.instant('PROGRESS.SUCCESS_MESSAGE'), { nzDuration: this.CELEBRATION_DURATION });

        // Trigger exit fade animation 800ms before component removal
        clearTimeout(this.exitStartTimeoutId);
        this.exitStartTimeoutId = setTimeout(() => {
          this.isCompletingExit = true;
        }, this.EXIT_START_DELAY);

        clearTimeout(this.celebrateTimeoutId);
        this.celebrateTimeoutId = setTimeout(() => {
          this.justCompleted = false;
          this.celebrationInProgress = false;
          this.isCompletingExit = false;
          this.emitProgressVisibility();
          this.progressReached100.emit(true); // parent hides AFTER celebration
        }, this.CELEBRATION_DURATION);
      } else {
        // ✅ Do NOT allow instant hide while celebration is running
        if (!this.celebrationInProgress) {
          this.progressReached100.emit(this.progress >= 100);
        }
      }

      this.emitProgressVisibility();

    });
  }

  get isProgressVisible(): boolean {
    return this.loaded && (this.progress < 100 || this.justCompleted);
  }

  private emitProgressVisibility(): void {
    this.setProgressVisibility(this.isProgressVisible);
  }

  private setProgressVisibility(visible: boolean): void {
    if (visible === this.reportedVisibility) return;

    this.reportedVisibility = visible;
    this.visibilityChange.emit(visible);
  }
  isFieldMissing(detail: MissingDetail): detail is FieldMissingDetail {
    return detail.type === 'field';
  }

  isPropertyMissing(detail: MissingDetail): detail is PropertyMissingDetail {
    return detail.type === 'property';
  }

  getMilestoneLabel(key: ProgressMilestoneKey, part: 'TITLE' | 'DESCRIPTION' | 'ACTION'): string {
    return `PROGRESS.MILESTONES.${key}.${part}`;
  }

  getFieldLabelKey(milestoneKey: ProgressMilestoneKey, fieldKey: string): string {
    return `PROGRESS.MILESTONES.${milestoneKey}.FIELDS.${fieldKey}`;
  }

  getHospitalitySectionList(sections: string[]): string {
    const translated = sections.map((section) =>
      this.translate.instant(`PROGRESS.HOSPITALITY_SECTION_LABELS.${section}`)
    );
    return translated.join(', ');
  }

  getPropertyMissingText(detail: PropertyMissingDetail): string {
    const address = detail.address && detail.address.trim().length
      ? detail.address
      : this.translate.instant('PROGRESS.MISSING.UNNAMED_PROPERTY');
    const sectionList = this.getHospitalitySectionList(detail.sections || []);
    return this.translate.instant('PROGRESS.MILESTONES.hospitalityProperties.PROPERTY_MISSING', {
      address,
      sections: sectionList
    });
  }

  getMilestoneRoute(key: ProgressMilestoneKey): string {
    return getMilestoneRoute(key);
  }

  getMilestoneQueryParams(key: ProgressMilestoneKey): Record<string, any> {
    return getMilestoneQueryParams(key, {
      autoOpenAddProperty: key === 'properties' && this.shouldAutoOpenAddPropertyModal()
    });
  }

  private shouldAutoOpenAddPropertyModal(): boolean {
    return this.loaded
      && this.progress < 100
      && this.incompleteMilestones.length === 1
      && this.incompleteMilestones[0]?.key === 'properties';
  }

  scrollAfterMilestoneNav(): void {
    // Wait until the navigation finishes, then scroll down to reveal routed content below progress
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        this.ngZone.runOutsideAngular(() => {
          // Give the routed component a tick to render and expand the page height
          setTimeout(() => {
            const mainContent = document.querySelector('.main-content') as HTMLElement | null;

            // 1) Try scrolling the layout container (if it's the real scroller)
            if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
              mainContent.scrollTo({ top: mainContent.scrollHeight, behavior: 'smooth' });
            }

            // 2) Always also scroll the window/document (covers cases where body is the real scroller)
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          }, 0);
        });
      });
  }
  openOnboardingVideoModal(): void {
    const vimeoBaseUrl = 'https://player.vimeo.com/video/';

    // Always show the German video, regardless of selected language
    const videoId = '1162911145';

    const fullUrl =
      `${vimeoBaseUrl}${videoId}` +
      `?autoplay=1&muted=1&playsinline=1&autopause=0&title=0&byline=0&portrait=0`;

    this.onboardingVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);

    this.modal.create({
      nzContent: this.onboardingVideoModal,
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: true,
      nzWrapClassName: 'scanafy-video-modal',
      nzCentered: true,
      nzWidth: 900,
      nzBodyStyle: { padding: '0px' }
    });
  }
  private triggerConfetti(): void {
    // Clear any previous scheduled bursts
    this.confettiTimeoutIds.forEach(clearTimeout);
    this.confettiTimeoutIds = [];

    const bursts = [
      { t: 0,    opts: { particleCount: 55, spread: 70,  startVelocity: 35, origin: { x: 0.5, y: 0.65 } } },
      { t: 350,  opts: { particleCount: 45, spread: 95,  startVelocity: 32, origin: { x: 0.25, y: 0.65 } } },
      { t: 750,  opts: { particleCount: 40, spread: 105, startVelocity: 30, origin: { x: 0.75, y: 0.65 } } },
      { t: 1200, opts: { particleCount: 35, spread: 120, startVelocity: 28, origin: { x: 0.5, y: 0.65 } } }
    ];

    bursts.forEach(b => {
      const id = setTimeout(() => confetti(b.opts as any), b.t);
      this.confettiTimeoutIds.push(id);
    });
  }
  handleMilestoneActionClick(event: MouseEvent, milestone: ProgressMilestone): void {
    event.preventDefault();
    event.stopPropagation();

    // If progress data not loaded yet, just navigate normally
    if (!this.loaded) {
      this.navigateToMilestone(milestone);
      return;
    }

    // Determine which milestones must be completed BEFORE we allow navigating to this action
    const missing = this.getMissingMilestonesBefore(milestone.key);

    // Only block for properties-related actions (the ones that lead into property creation)
    const isPropertiesAction =
      milestone.key === 'properties' || milestone.key === 'hospitalityProperties';

    if (isPropertiesAction && missing.length > 0) {
      this.pendingTargetMilestone = milestone;
      this.missingBeforeTarget = missing;

      this.modal.create({
        nzTitle: this.translate.instant('PROGRESS.BLOCK_PROPERTIES.TITLE'),
        nzContent: this.missingStepsModal,
        nzFooter: [
          {
            label: this.translate.instant('PROGRESS.BLOCK_PROPERTIES.GO_FIRST'),
            type: 'primary',
            onClick: () => {
              const firstMissing = this.missingBeforeTarget[0];
              if (firstMissing) this.navigateToMilestone(firstMissing);
              this.modal.closeAll();
            }
          },
          {
            label: this.translate.instant('PROGRESS.BLOCK_PROPERTIES.CLOSE'),
            onClick: () => this.modal.closeAll()
          }
        ],
        nzClosable: true,
        nzMaskClosable: true
      });

      return;
    }

    // Allowed -> proceed
    this.navigateToMilestone(milestone);
  }

  private getMissingMilestonesBefore(targetKey: ProgressMilestoneKey): ProgressMilestone[] {
    const isPropertiesAction =
      targetKey === 'properties' || targetKey === 'hospitalityProperties';

    if (!isPropertiesAction) return [];

    const missing: ProgressMilestone[] = [];
    if (!this.isCompanyInfoComplete && this.companyInfoMilestone) {
      missing.push(this.companyInfoMilestone);
    }
    if (!this.isLandingPageComplete && this.landingPageMilestone) {
      missing.push(this.landingPageMilestone);
    }

    return missing;
  }

  private navigateToMilestone(milestone: ProgressMilestone): void {
    const route = this.getMilestoneRoute(milestone.key);
    const queryParams = this.getMilestoneQueryParams(milestone.key);

    this.router.navigate([route], { queryParams }).then(() => {
      this.scrollAfterMilestoneNav();
    });
  }
}
